import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, ArrowLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { computeAgencyWeeklyMatrix } from '../utils/visitCalculations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisitHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const metric = searchParams.get('metric') === 'projected' ? 'projected' : 'final';
  const [visits, setVisits] = useState([]);
  const [projections, setProjections] = useState([]);
  const [trackingConfig, setTrackingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agencyQuery, setAgencyQuery] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [visitsRes, projRes, cfgRes] = await Promise.all([
          axios.get(`${API}/visits`, { headers }),
          axios.get(`${API}/projections`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/tracking-config`, { headers }).catch(() => ({ data: null })),
        ]);
        setVisits(visitsRes.data || []);
        setProjections(projRes.data || []);
        setTrackingConfig(cfgRes.data);
      } catch (e) {
        toast.error('Failed to load visit history');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const { weeks, rows, totals } = useMemo(
    () => computeAgencyWeeklyMatrix(visits, projections, trackingConfig, metric, 12, agencyQuery),
    [visits, projections, trackingConfig, metric, agencyQuery]
  );

  const isProjected = metric === 'projected';
  const grandTotal = totals.reduce((a, b) => a + b, 0);

  // % change comparing 12 weeks ago (first column) to the last week (last column).
  const pctChange = (first, last) => {
    // No baseline 12 weeks ago → % change is undefined; show a dash rather than
    // a misleading "New"/huge number.
    if (first === 0) return { label: '—', cls: 'text-muted-foreground', Icon: Minus };
    const pct = Math.round(((last - first) / first) * 100);
    if (pct === 0) return { label: '0%', cls: 'text-muted-foreground', Icon: Minus };
    if (pct > 0) return { label: `+${pct}%`, cls: 'text-green-600', Icon: TrendingUp };
    return { label: `${pct}%`, cls: 'text-red-600', Icon: TrendingDown };
  };
  const nWeeks = weeks.length;
  const renderPctCell = (values, testid) => {
    const first = values[0] ?? 0;
    const last = values[nWeeks - 1] ?? 0;
    const c = pctChange(first, last);
    const CIcon = c.Icon;
    const firstLabel = weeks[0]?.label || '';
    const lastLabel = weeks[nWeeks - 1]?.label || '';
    const tip = `${firstLabel}: ${first} → ${lastLabel}: ${last}`;
    return (
      <td className={`px-3 py-3 text-right tabular-nums whitespace-nowrap border-l ${c.cls}`} data-testid={testid} title={tip}>
        <span className="inline-flex items-center gap-1 justify-end">
          <CIcon size={14} /> {c.label}
        </span>
      </td>
    );
  };

  return (
    <div className="space-y-6" data-testid="visit-history-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="visit-history-back">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            12-Week {isProjected ? 'Projected' : 'Final'} History
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isProjected
              ? 'Projected counts by agency for the last 12 weeks.'
              : 'Final (actual) counts by agency for the last 12 completed weeks.'}
          </p>
        </div>
      </div>

      <Card className="p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Filter by agency (e.g. New Day, Pathfinder)..."
            value={agencyQuery}
            onChange={(e) => setAgencyQuery(e.target.value)}
            className="pl-10"
            data-testid="visit-history-search"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="visit-history-empty">
            No {isProjected ? 'projected' : 'final'} data found for the last 12 weeks.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium sticky left-0 bg-muted/50 z-10 min-w-[180px]">
                    Agency
                  </th>
                  {weeks.map((w) => (
                    <th key={w.weekEnding} className="px-3 py-3 text-right font-medium tabular-nums whitespace-nowrap">
                      {w.label}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-right font-medium whitespace-nowrap border-l">
                    vs 12 wks ago
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Total row pinned directly under the date header row */}
                <tr className="border-t-2 border-b bg-primary/5 font-semibold" data-testid="visit-history-total-row">
                  <td className="px-4 py-3 text-left sticky left-0 bg-[#eef1f6] z-10">
                    Total ({grandTotal})
                  </td>
                  {totals.map((t, i) => (
                    <td key={i} className="px-3 py-3 text-right tabular-nums">{t || '-'}</td>
                  ))}
                  {renderPctCell(totals, 'visit-history-total-pct')}
                </tr>
                {rows.map((r) => (
                  <tr key={r.agency} className="border-t hover:bg-muted/30" data-testid={`history-agency-${r.agency}`}>
                    <td className="px-4 py-3 text-left font-medium sticky left-0 bg-background z-10">
                      {r.agency}
                    </td>
                    {r.values.map((v, i) => (
                      <td key={i} className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                        {v || '-'}
                      </td>
                    ))}
                    {renderPctCell(r.values, `history-pct-${r.agency}`)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
